import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DimensionedNumberInput } from "common/components/DimensionedNumberInput";
import { EditBoundary } from "./EditBoundary";
import {
  NewZoneKind,
  PendingZone,
  updatePendingZone,
} from "portal/state/farmExplorer";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelf } from "portal/state/store";
import React, { FC } from "react";

export const NEW_ZONE_TYPE_I18N_KEYS: Record<NewZoneKind, string> = {
  farmBoundary: "views.farms.zoneTypes.farmBoundary",
  obstacle: "views.farms.zoneTypes.obstacle",
  centerPivot: "views.farms.zoneTypes.centerPivot_one",
};

const INTRO_I18N_KEYS: Record<NewZoneKind, string> = {
  farmBoundary: "views.farms.setFarmBoundary.introMessage",
  obstacle: "views.farms.newObstacle.introMessage",
  centerPivot: "views.farms.newCenterPivot.introMessage",
};

const NAME_I18N_KEYS: Partial<Record<NewZoneKind, string>> = {
  obstacle: "views.farms.newObstacle.name",
  centerPivot: "views.farms.newCenterPivot.name",
};

interface NewZoneDetailsProps {
  pending: PendingZone;
}

export const NewZoneDetails: FC<NewZoneDetailsProps> = ({ pending }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { measurementSystem } = useSelf();
  const { kind, name, passable, armLengthMeters } = pending;
  const nameI18nKey = NAME_I18N_KEYS[kind];

  return (
    <div className="flex flex-col gap-3 px-2 pb-2">
      <Typography component="div" className="text-sm">
        <Trans t={t} i18nKey={INTRO_I18N_KEYS[kind]} />
      </Typography>
      {nameI18nKey && (
        <TextField
          label={t(nameI18nKey)}
          value={name}
          required
          size="small"
          onChange={(event) =>
            dispatch(updatePendingZone({ name: event.target.value }))
          }
        />
      )}
      {kind === "obstacle" && (
        <div className="flex flex-col">
          <FormControlLabel
            control={
              <Checkbox
                checked={passable}
                onChange={(_event, checked) =>
                  dispatch(updatePendingZone({ passable: checked }))
                }
              />
            }
            label={t("utils.descriptors.passable")}
          />
          <Typography className="text-xs" color="textSecondary">
            {t("views.farms.newObstacle.passableHelp")}
          </Typography>
        </div>
      )}
      {kind === "centerPivot" && (
        <DimensionedNumberInput
          label={t("views.farms.newCenterPivot.armLength")}
          storedValue={armLengthMeters ?? 0}
          storedUnit="m"
          displayUnits={{ imperial: "ft", metric: "m" }}
          localSystem={measurementSystem}
          minStoredValue={0}
          roundDisplayToNPlaces={1}
          onChange={(stored) =>
            dispatch(
              updatePendingZone({ armLengthMeters: stored || undefined })
            )
          }
        />
      )}
      <EditBoundary />
    </div>
  );
};
